// @ts-nocheck

import { useEffect, useCallback } from 'react';

import { pick, omit } from 'lodash';

import type { Game } from '@shared/types';

import useAxios from '@services/api';

import { GameBuild } from './client.types';
import type { SubmittedForm, CropPixelsData } from './client.types';
import { cropImage, rotateImages, resizeImage } from './client.utils';

const fixedPixels = { x: 100, y: 100, width: 200, height: 200 };

export const useFetchGameDefaults = (name: string) => {
  const { response, error, issueRequest } = useAxios<Game>();

  useEffect(() => {
    void issueRequest({ url: `/games/${name}`, method: 'get' });
  }, [name, issueRequest]);

  return response;
};

export const useFetchGameDefaultsLazy = () => {
  const { response, error, issueRequest, loading } = useAxios<Game>();

  const useFetchGameDefaultsLazy = useCallback((name: string) => {
    void issueRequest({ url: `/games/${name}`, method: 'get' });
  }, [issueRequest]);

  return [response, useFetchGameDefaultsLazy, loading] as const;
};

export const useFetchGameBuild = () => {

  const { response, error, issueRequest } = useAxios();

  const fetchGameBuild = useCallback((gameId: string) => {
    void issueRequest({ url: `/games/build/${gameId}`, method: 'get' });
    // void issueRequest({ url: `/game/build/${id}`, method: 'get', headers: { 'x-api-key': '38f8ef5e-8c83-416d-9b6d-bd9980c44429'} });
  }, [issueRequest]);
  return [response, fetchGameBuild, error] as const;
};

// export const useFetchGameBuild = (id: string) => {
//   const { response, error, issueRequest } = useAxios<GameBuild>();

//   useEffect(() => {
//     if (!id) return;  // Проверка на пустой ID

//     void issueRequest({ 
//       url: `/game/build/${id}`, 
//       method: 'get', 
//       headers: {
//         'x-api-key': '38f8ef5e-8c83-416d-9b6d-bd9980c44429',
//       },
//     });
//   }, [id]);

//   return [response, error] as const;
// };


export const useCreateGameBuild = () => {
  const { response, error, issueRequest, loading, setLoading } =
    useAxios<GameBuild>();

  const handleSubmit = useCallback(
    (
      data: SubmittedForm,
      croppedPixelsData: CropPixelsData,
      tiles: string[]
    ) => {
      setLoading(true);

      // Для слотов проверки обязательности картинок отключены по требованиям

      const pendingPromises: Promise<void>[] = [];

      for (const [fieldName, pixels] of Object.entries(croppedPixelsData)) {
        if (!pixels) {
          continue;
        }

        // Поддержка имён вида "gameTiles.X" для тайлов
        const tileMatch = /^gameTiles\.(\d+)$/.exec(fieldName);
        if (tileMatch) {
          const tileIndex = parseInt(tileMatch[1], 10);
          const tileValue = data.gameTiles?.[tileIndex];
          if (tileValue instanceof File || typeof tileValue === 'string') {
            pendingPromises.push(
              cropImage(tileValue, pixels).then((croppedImage) => {
                data.gameTiles[tileIndex] = croppedImage as any;
              })
            );
          }
          continue;
        }

        // Также поддержим случай, когда ключ — просто числовой индекс строки
        if (!isNaN(parseInt(fieldName as any, 10))) {
          const numericIndex = parseInt(fieldName as any, 10);
          const tileValue = data.gameTiles?.[numericIndex];
          if (tileValue instanceof File || typeof tileValue === 'string') {
            pendingPromises.push(
              cropImage(tileValue, pixels).then((croppedImage) => {
                data.gameTiles[numericIndex] = croppedImage as any;
              })
            );
          }
          continue;
        }

        // Обычные поля изображений
        const fieldValue = (data as any)[fieldName];
        if (fieldValue instanceof File || typeof fieldValue === 'string') {
          pendingPromises.push(
            cropImage(fieldValue, pixels).then((croppedImage) => {
              (data as any)[fieldName] = croppedImage as any;
            })
          );
        }
      }

      return Promise.all(pendingPromises)
        .then(() => {
          // НЕ ПЕРЕЗАПИСЫВАЕМ gameTiles - используем только те, что уже есть в data
          console.log('Using existing gameTiles without rotation:', data.gameTiles);

          // Убеждаемся, что gameTiles существует и является массивом
          if (!data.gameTiles || !Array.isArray(data.gameTiles)) {
            console.error('gameTiles is missing or not an array:', data.gameTiles);
            data.gameTiles = Array(14).fill('') as any;
          }

          const formData = new FormData();

          const fileFields = [
            'startGameImage',
            'endGameImage',
            'megaWinImage',
            'gameTiles',
            'customMusicFile',
            'urlButton',
          ];
          const fileUrls = <(string | null)[]>[];
          const gameTilesUrls = <(string | null)[]>[];

          console.log('Initial data:', data);
          console.log('Initial data.customMusicFile:', data.customMusicFile);
          console.log('Initial data type:', typeof data.customMusicFile);
          console.log('Initial data instanceof File:', data.customMusicFile instanceof File);

          const files: Pick<
            SubmittedForm,
            'startGameImage' | 'endGameImage' | 'megaWinImage' | 'gameTiles' | 'customMusicFile' | 'urlButton'
          > = pick(data, fileFields);
          const plainFields: Omit<
            SubmittedForm,
            'startGameImage' | 'endGameImage' | 'megaWinImage' | 'customMusicFile' | 'gameTiles' | 'urlButton'
          > & { gameTiles: (string | null)[] } = omit(data, [...fileFields]);

          console.log('Files to process:', files);

          const urlFieldMap: Record<string, keyof typeof plainFields> = {
            startGameImage: 'startGameImageUrl' as any,
            endGameImage: 'endGameImageUrl' as any,
            megaWinImage: 'moneyWinImageUrl' as any,
            urlButton: 'urlButton' as any,
          };

          for (const [key, fileObject] of Object.entries(files)) {
            const field = key as keyof typeof files;
            console.log(`Processing field: ${field}`, fileObject);
            if (field === 'gameTiles') {
              for (let i = 0; i < files[field].length; i++) {
                const imageFile = files[field][i];
                console.log(`Processing gameTile ${i}:`, imageFile);
                if (typeof imageFile === 'string' && imageFile && imageFile !== '') {
                  gameTilesUrls.push(imageFile);
                  console.log(`Added URL for gameTile[${i}]:`, imageFile);
                } else if (imageFile instanceof File) {
                  gameTilesUrls.push(null);
                  formData.append(`gameTiles[${i}]`, imageFile);
                  console.log(`Added gameTile[${i}] to FormData`);
                } else {
                  gameTilesUrls.push(null);
                  console.log(`Empty tile at position ${i}`);
                }
              }
            } else if (fileObject instanceof File) {
              // Специальная обработка для urlButton: если File - отправляем как fileDownload
              if (field === 'urlButton') {
                formData.append('fileDownload', fileObject);
                (plainFields as any).urlButton = ''; // Очищаем urlButton, так как используется файл
                console.log('Added urlButton file as fileDownload:', fileObject.name, 'size:', fileObject.size);
              } else {
                formData.append(field, fileObject);
                console.log(`Added ${field} to FormData`);
              }
            } else if (typeof fileObject === 'string') {
              if (field === 'customMusicFile') {
                console.log('Adding musicUrl to plainFields:', fileObject);
                (plainFields as any).musicUrl = fileObject;
              } else if (field === 'urlButton') {
                console.log('Adding urlButton to plainFields:', fileObject);
                (plainFields as any).urlButton = fileObject;
                (plainFields as any).fileDownloadUrl = ''; // Очищаем fileDownloadUrl, так как используется URL
              } else {
                // Передаём URL (включая пустую строку) в JSON для серверного апдейта/очистки
                const urlField = urlFieldMap[field as string];
                if (urlField) {
                  (plainFields as any)[urlField] = fileObject;
                }
              }
            }
          }

          while (gameTilesUrls.length < 14) {
            gameTilesUrls.push(null);
          }
          plainFields.gameTiles = gameTilesUrls.map((url) => url || '');
          console.log('Final gameTiles array:', plainFields.gameTiles);

          const jsonData = JSON.stringify(plainFields);
          console.log('JSON data to send:', jsonData);
          console.log('plainFields before JSON:', plainFields);

          formData.append('jsonData', jsonData);

          for (const pair of formData.entries()) {
            console.log('FormData entry:', pair[0], pair[1]);
          }

          return issueRequest({
            url: '/games/build/slot',
            method: 'put',
            body: formData,
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });
        })
        .catch((error) => {
          console.error('Error processing files:', error);
          setLoading(false);
        });
    },
    [issueRequest]
  );

  return [response, handleSubmit, loading] as const;
};

export const useSetActivateGameDate = () => {
  const { response, error, issueRequest } = useAxios<Record<string, never>>();

  const useSetActivateGameDate = useCallback((id: string) => {
    void issueRequest({ url: `/games/keys/${id}`, method: 'put' });
  }, []);

  return [response, useSetActivateGameDate] as const;
};
